// biome-ignore-all lint/suspicious/noArrayIndexKey: the preview list is fully replaced on each parsed plan (never reordered or mutated mid-render) — same rationale as ImportPage (Task #32).
// =============================================================================
// DeliveryAddressImport.jsx — Unleashed Delivery-Address CSV import (Phase 4a)
// =============================================================================
// Self-contained section rendered at the bottom of ImportPage: upload the
// Unleashed "Delivery Address" export → preview the plan (what will be created,
// what's skipped and why) → Import. All decision logic is the PURE planner in
// src/services/deliveryImport.ts (unit-tested; idempotent re-runs); this
// component only does I/O: read file, load the dedupe baseline, insert, audit.

import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { insertAddresses, listAddressesByShelterIds } from "../../data/deliveryAddresses";
import { listAllShelters } from "../../data/shelters";
import { parseDeliveryCsv, planDeliveryImport } from "../../services/deliveryImport";
import { logAudit } from "../../utils/auditLog";
import { GLYPH } from "../../utils/glyphs";
import { logger } from "../../utils/logger";
import { Btn, Card } from "../../utils/ui.jsx";

// A collapsible list of the rows behind a data-quality count. The counts alone
// tell you something needs fixing in Unleashed but not WHICH rows — with ~94 of
// them that's unworkable, so each count can be expanded to the actual rows, with
// the file line number for finding them in Excel.
function IssueRows({ label, rows, columns }) {
  const [copied, setCopied] = React.useState(false);
  if (!rows || rows.length === 0) return null;

  // Tab-separated so it pastes straight into Excel as columns — these get fixed
  // in Unleashed, so getting them OUT of here matters more than reading on screen.
  async function copyRows() {
    const head = ["Line", ...columns.map((c) => c.label)].join("\t");
    const body = rows
      .map((r) => [r.csvLine, ...columns.map((c) => r[c.key] || "")].join("\t"))
      .join("\n");
    try {
      await navigator.clipboard.writeText(`${head}\n${body}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      setCopied(false);
    }
  }

  return (
    <details className="mb-2">
      <summary className="cursor-pointer text-[12px] font-semibold" style={{ color: "#B8860B" }}>
        Show the {rows.length} {label}
      </summary>
      <div className="mt-2">
        <button
          type="button"
          onClick={copyRows}
          className="bg-transparent border-none cursor-pointer text-[12px] text-zephyr-teal-dark px-0 mb-1"
        >
          {copied ? "Copied ✓" : "Copy for Excel"}
        </button>
        <div
          className="border border-zephyr-border rounded-[8px] overflow-y-auto text-[12px] bg-white"
          style={{ maxHeight: 240 }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={cellTh}>Line</th>
                {columns.map((c) => (
                  <th key={c.key} style={cellTh}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={cellTd}>{r.csvLine}</td>
                  {columns.map((c) => (
                    <td key={c.key} style={cellTd}>
                      {r[c.key] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}

const cellTh = {
  textAlign: "left",
  padding: "5px 8px",
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid #E0EDEB",
  position: "sticky",
  top: 0,
  background: "#F7F9F9",
};
const cellTd = { padding: "4px 8px", fontSize: 12, borderBottom: "1px solid #F0F0F0" };

// Column sets for the three flagged-row lists.
const ADDRESS_COLS = [
  { key: "shelterCode", label: "Code" },
  { key: "addressName", label: "Address name" },
  { key: "street", label: "Street" },
  { key: "suburb", label: "Suburb" },
  { key: "city", label: "Town/City" },
  { key: "state", label: "State" },
  { key: "postcode", label: "Postcode" },
];

export function DeliveryAddressImport() {
  const { user } = useSession();
  const [step, setStep] = React.useState("upload"); // upload | preview | importing | done
  const [err, setErr] = React.useState("");
  const [plan, setPlan] = React.useState(null);
  const [result, setResult] = React.useState(null);
  // shelter_id → Customer Code, so the preview can show the human code (planned
  // rows carry only shelter_id since db-83).
  const [codeById, setCodeById] = React.useState({});

  function reset() {
    setStep("upload");
    setErr("");
    setPlan(null);
    setResult(null);
    setCodeById({});
  }

  function handleFile(e) {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async function (ev) {
      const { rows, error } = parseDeliveryCsv(String(ev.target.result));
      if (error) {
        setErr(error);
        return;
      }
      // Dedupe baseline: which shelters exist, and their current addresses
      // (incl. deactivated, so a re-import can't resurrect a soft-deleted one).
      const shelters = await listAllShelters();
      if (!shelters) {
        setErr("Couldn't load the shelters list — try again.");
        return;
      }
      const shelterIdByCode = {};
      const codeByShelterId = {};
      shelters.forEach(function (s) {
        if (s.shelter_code) shelterIdByCode[s.shelter_code] = s.id;
        if (s.id) codeByShelterId[s.id] = s.shelter_code;
      });
      setCodeById(codeByShelterId);
      const fileShelterIds = Array.from(
        new Set(
          rows
            .map(function (r) {
              return shelterIdByCode[r.shelter_code];
            })
            .filter(Boolean)
        )
      );
      const existing = await listAddressesByShelterIds(fileShelterIds);
      if (existing === null) {
        setErr("Couldn't load existing delivery addresses — try again.");
        return;
      }
      setPlan(planDeliveryImport(rows, shelterIdByCode, existing));
      setStep("preview");
    };
    reader.readAsText(file);
  }

  async function runImport() {
    if (!plan) return;
    setStep("importing");
    let created = 0;
    let failed = 0;
    for (let i = 0; i < plan.creates.length; i += 100) {
      const chunk = plan.creates.slice(i, i + 100);
      const res = await insertAddresses(chunk);
      if (res.error) {
        failed += chunk.length;
        logger.warn("Delivery import chunk failed:", res.error.message);
      } else {
        created += chunk.length;
      }
    }
    logAudit("delivery_addresses_imported", {
      created: created,
      failed: failed,
      skipped_existing: plan.skippedExisting,
      unknown_shelters: plan.unknownShelters.length,
      by: user?.email || "",
    });
    setResult({ created: created, failed: failed });
    setStep("done");
  }

  return (
    <Card style={{ padding: 24, marginTop: 24 }}>
      <div className="text-[15px] font-bold text-zephyr-teal-dark mb-1">
        Step 2 — Import Delivery Addresses
      </div>
      <div className="text-[13px] text-zephyr-muted mb-4">
        Upload the Unleashed <b>Delivery Address</b> export (Customers → Delivery Addresses →
        Export). Safe to re-run — addresses the portal already has are skipped.
      </div>

      {step === "upload" && (
        <div>
          {/* Native file inputs can't be styled — hidden input + a link-styled
              label (bold + underlined so it's obviously clickable, John). */}
          <label
            htmlFor="delivery-import-file"
            className="cursor-pointer font-bold underline text-zephyr-teal-dark text-[13px]"
          >
            📄 Choose the Delivery Address CSV…
          </label>
          <input
            id="delivery-import-file"
            type="file"
            accept=".csv,.txt"
            aria-label="Delivery address CSV file"
            onChange={handleFile}
            className="hidden"
          />
          {err && <div className="text-zephyr-red mt-3 text-[13px]">{err}</div>}
        </div>
      )}

      {step === "preview" && plan && (
        <div>
          <div className="flex gap-6 text-[13px] flex-wrap mb-3">
            <span>
              <strong style={{ color: "#2E7D32" }}>{plan.creates.length}</strong> addresses to
              create
            </span>
            <span>
              <strong>{plan.defaultsAssigned}</strong> will become their DFV Service's default
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.skippedExisting}</strong> already in the
              portal (skipped)
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.obsoleteSkipped}</strong> obsolete
              (skipped)
            </span>
            <span>
              <strong style={{ color: plan.invalidRows ? "#C62828" : "#607D79" }}>
                {plan.invalidRows}
              </strong>{" "}
              invalid rows
            </span>
          </div>
          {plan.unknownShelters.length > 0 && (
            <div className="text-[13px] mb-3" style={{ color: "#B8860B" }}>
              ⚠️ {plan.unknownShelters.length} shelter code(s) in the file don't exist in the portal
              and will be skipped: {plan.unknownShelters.join(", ")}
            </div>
          )}
          {(plan.blankCity > 0 || plan.residualSuburb > 0) && (
            <div className="text-[13px] mb-3" style={{ color: "#B8860B" }}>
              ⚠️ Data quality:{" "}
              {plan.blankCity > 0 && (
                <span>
                  <strong>{plan.blankCity}</strong> row(s) have a blank Town/City (would import a
                  blank Suburb/City)
                </span>
              )}
              {plan.blankCity > 0 && plan.residualSuburb > 0 && "; "}
              {plan.residualSuburb > 0 && (
                <span>
                  <strong>{plan.residualSuburb}</strong> row(s) still have a Suburb value (the
                  Unleashed cleanup may be unfinished — Suburb should be empty)
                </span>
              )}
              .
              {/* The rows behind each count — these are fixed in Unleashed, so the
                  list is expandable and copyable rather than just a number. */}
              <div className="mt-2">
                <IssueRows
                  label="rows with a blank Town/City"
                  rows={plan.blankCityRows}
                  columns={ADDRESS_COLS}
                />
                <IssueRows
                  label="rows that still have a Suburb value"
                  rows={plan.residualSuburbRows}
                  columns={ADDRESS_COLS}
                />
              </div>
            </div>
          )}
          {plan.invalidRows > 0 && (
            <div className="text-[13px] mb-3" style={{ color: "#C62828" }}>
              ⚠️ {plan.invalidRows} row(s) can't be imported — a row needs both a Customer Code and a
              Street Address.
              <div className="mt-2">
                <IssueRows
                  label="invalid rows"
                  rows={plan.invalidRowDetails}
                  columns={ADDRESS_COLS}
                />
              </div>
            </div>
          )}
          {plan.creates.length > 0 && (
            <div
              className="border border-zephyr-border rounded-[8px] overflow-y-auto mb-4 text-[12px]"
              style={{ maxHeight: 220 }}
            >
              {plan.creates.slice(0, 50).map(function (a, i) {
                return (
                  <div
                    key={`${a.shelter_id}-${i}`}
                    className="py-[6px] px-3"
                    style={{ borderTop: i > 0 ? "1px solid #EEF3F2" : "none" }}
                  >
                    <b>{codeById[a.shelter_id] || a.shelter_id}</b> — {a.address_name}:{" "}
                    {[a.street, a.suburb_city || a.suburb, a.state, a.postcode]
                      .filter(Boolean)
                      .join(", ")}
                    {a.is_default && (
                      <span className="ml-2 text-[10px] font-bold text-white bg-zephyr-teal rounded-[3px] py-[1px] px-[5px]">
                        DEFAULT
                      </span>
                    )}
                  </div>
                );
              })}
              {plan.creates.length > 50 && (
                <div className="py-[6px] px-3 text-zephyr-muted">
                  … and {plan.creates.length - 50} more
                </div>
              )}
            </div>
          )}
          <div className="flex gap-[10px]">
            <Btn variant="secondary" onClick={reset}>
              Cancel
            </Btn>
            <Btn onClick={runImport} disabled={plan.creates.length === 0}>
              Import {plan.creates.length} address{plan.creates.length === 1 ? "" : "es"}
            </Btn>
          </div>
        </div>
      )}

      {step === "importing" && <div className="text-[13px] text-zephyr-muted">Importing…</div>}

      {step === "done" && result && (
        <div>
          <div className="text-[13px] mb-3">
            <span style={{ color: "#2E7D32", fontWeight: 700 }}>
              {result.created} address{result.created === 1 ? "" : "es"} created {GLYPH.check}
            </span>
            {result.failed > 0 && (
              <span className="ml-3 text-zephyr-red font-semibold">
                {result.failed} failed — check the console / try re-running (already-created rows
                will be skipped).
              </span>
            )}
          </div>
          <Btn variant="secondary" onClick={reset}>
            Import another file
          </Btn>
        </div>
      )}
    </Card>
  );
}
